// src/modules/serviceRecords/hooks/useServiceForm.js
// Servis kayıt formu veri yönetimi hook'u - Refactored

import { useState, useEffect, useMemo } from 'react';
import { db } from '../../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import useCustomerOptions from '../../../hooks/useCustomerOptions';
import useCachedCollection, { invalidateCollections } from '../../../hooks/useCachedCollection';
import { useNavigate } from 'react-router-dom';
import { createServiceRecord, updateServiceRecord, buildServiceRecordData } from './serviceFormHelpers';

export const useServiceForm = (id) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(id);
    const [loading, setLoading] = useState(isEditMode);
    const [isSaving, setIsSaving] = useState(false);

    // Müşteri ve Cihaz seçim listeleri: paylaşımlı oturum cache'i.
    // Eskiden bu form her açılışta customers (1324) ve devices (1156) koleksiyonlarını
    // baştan okuyup iki kalıcı realtime listener bırakıyordu — form açılışı başına
    // ~2500 doküman. İkisi de yalnızca seçim listesi besliyor.
    const { customers } = useCustomerOptions();
    const { data: devicesRaw } = useCachedCollection('devices');

    const devices = useMemo(
        () => [...devicesRaw].sort((a, b) => (a.seriNumarasi || '').localeCompare(b.seriNumarasi || '', 'tr')),
        [devicesRaw]
    );

    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [selectedDeviceId, setSelectedDeviceId] = useState('');

    // Form alanları
    const [arizaTanimi, setArizaTanimi] = useState('');
    const [yapilanIslemler, setYapilanIslemler] = useState('');
    const [kullanilanParcalar, setKullanilanParcalar] = useState([]);

    // Ödeme bilgileri
    const [toplamTutar, setToplamTutar] = useState(0);
    const [odenenTutar, setOdenenTutar] = useState(0);
    const [odemeTipi, setOdemeTipi] = useState('Nakit');
    const [servisNotlari, setServisNotlari] = useState('');
    const [teslimTarihi, setTeslimTarihi] = useState(null);

    // Yeni Alanlar
    const [bakimTuru, setBakimTuru] = useState('');
    const [oksijenOrani, setOksijenOrani] = useState('');
    const [basincDegeri, setBasincDegeri] = useState('');
    const [calismaSuresi, setCalismaSuresi] = useState('');

    // FAZ 4 - Ek Teknik Alanlar
    const [cihazHurdada, setCihazHurdada] = useState(false);
    const [teslimAlmaTarihi, setTeslimAlmaTarihi] = useState(null);
    const [teslimAlmadim, setTeslimAlmadim] = useState(false);
    const [teslimSaati, setTeslimSaati] = useState('');

    // Cihaz seçildiğinde müşteriyi otomatik seç
    const handleDeviceSelect = (deviceId, deviceList = devices) => {
        setSelectedDeviceId(deviceId);
        if (deviceId) {
            const device = deviceList.find(d => d.id === deviceId);
            if (device && device.musteriId) {
                if (selectedCustomerId !== device.musteriId) {
                    setSelectedCustomerId(device.musteriId);
                }
            }
        }
    };

    // Müşteri seçildiğinde cihaz seçimini yönet
    const handleCustomerSelect = (customerId) => {
        setSelectedCustomerId(customerId);
        if (selectedDeviceId) {
            const device = devices.find(d => d.id === selectedDeviceId);
            if (device && device.musteriId !== customerId) {
                setSelectedDeviceId('');
            }
        }
    };

    // Düzenleme modu için verileri çek
    useEffect(() => {
        let isMounted = true;

        if (isEditMode) {
            setLoading(true);

            const fetchServiceRecord = async () => {
                const timeoutPromise = new Promise((_, reject) =>
                    setTimeout(() => reject(new Error("Veri çekme işlemi zaman aşımına uğradı!")), 10000)
                );

                try {
                    const docRef = doc(db, "serviceRecords", id);
                    const docSnap = await Promise.race([getDoc(docRef), timeoutPromise]);

                    if (!isMounted) return;

                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setSelectedCustomerId(data.musteriId);
                        setSelectedDeviceId(data.cihazId);
                        setTeslimTarihi(data.teslimTarihi ? data.teslimTarihi.toDate() : null);
                        setArizaTanimi(data.arizaTanimi || '');
                        setYapilanIslemler(data.yapilanIslemler || '');

                        if (data.kullanilanParcalar?.length) {
                            if (data.kullanilanParcalar[0].aciklama) {
                                setKullanilanParcalar(data.kullanilanParcalar.map(p => ({
                                    tip: 'manual', ad: p.aciklama, miktar: 1,
                                    birimFiyat: p.fiyat || 0, toplamFiyat: p.fiyat || 0
                                })));
                            } else {
                                setKullanilanParcalar(data.kullanilanParcalar);
                            }
                        }

                        setOdenenTutar(data.odenenTutar || 0);
                        setOdemeTipi(data.odemeTipi || 'Nakit');
                        setServisNotlari(data.servisNotlari || '');
                        setBakimTuru(data.bakimTuru || '');
                        setOksijenOrani(data.oksijenOrani || '');
                        setBasincDegeri(data.basincDegeri || '');
                        setCalismaSuresi(data.calismaSuresi || '');
                        setCihazHurdada(data.cihazHurdada || false);
                        setTeslimAlmaTarihi(data.teslimAlmaTarihi ? data.teslimAlmaTarihi.toDate() : null);
                        setTeslimAlmadim(data.teslimAlmadim || false);
                        setTeslimSaati(data.teslimSaati || '');
                    } else {
                        alert("Düzenlenecek kayıt bulunamadı!");
                        if (isMounted) navigate('/servis-kayitlari');
                    }
                } catch (error) {
                    console.error("Veri çekme hatası:", error);
                    alert(`Kayıt yüklenirken bir hata oluştu: ${error.message}`);
                    if (isMounted) navigate('/servis-kayitlari');
                } finally {
                    if (isMounted) setLoading(false);
                }
            };

            fetchServiceRecord();
        } else {
            setLoading(false);
        }

        return () => { isMounted = false; };
    }, [id, isEditMode, navigate]);

    // Toplam tutarı hesapla
    useEffect(() => {
        const total = kullanilanParcalar.reduce((sum, p) => sum + (Number(p.toplamFiyat) || 0), 0);
        setToplamTutar(total);
    }, [kullanilanParcalar]);

    // Malzeme ekleme
    const handleMaterialSelect = (items) => {
        if (Array.isArray(items)) {
            setKullanilanParcalar([...kullanilanParcalar, ...items]);
        } else {
            setKullanilanParcalar([...kullanilanParcalar, items]);
        }
    };

    const handleRemoveParca = (index) => {
        const newData = [...kullanilanParcalar];
        newData.splice(index, 1);
        setKullanilanParcalar(newData);
    };

    const handleMiktarChange = (index, newMiktar) => {
        const newData = [...kullanilanParcalar];
        const item = newData[index];
        item.miktar = Number(newMiktar);
        item.toplamFiyat = item.miktar * item.birimFiyat;
        setKullanilanParcalar(newData);
    };

    const handleFiyatChange = (index, newFiyat) => {
        const newData = [...kullanilanParcalar];
        const item = newData[index];
        item.birimFiyat = Number(newFiyat);
        item.toplamFiyat = item.miktar * item.birimFiyat;
        setKullanilanParcalar(newData);
    };

    // Form gönderme
    const handleSubmit = async () => {
        if (!selectedCustomerId) {
            alert("Lütfen Müşteri seçimi yapınız.");
            return;
        }
        if (!selectedDeviceId || !teslimTarihi) {
            alert("Servis kaydı için Cihaz ve Planlanan Teslim Tarihi seçimi zorunludur.");
            return;
        }

        const customer = customers.find(c => c.id === selectedCustomerId);
        const device = devices.find(d => d.id === selectedDeviceId);

        const serviceRecordData = buildServiceRecordData({
            customer, device, teslimTarihi, teslimAlmaTarihi,
            arizaTanimi, yapilanIslemler, kullanilanParcalar,
            toplamTutar, odenenTutar, odemeTipi, servisNotlari,
            bakimTuru, oksijenOrani, basincDegeri, calismaSuresi,
            cihazHurdada, teslimAlmadim, teslimSaati
        });

        try {
            setIsSaving(true);
            if (isEditMode) {
                await updateServiceRecord(id, serviceRecordData);
                await invalidateCollections(['serviceRecords']);
                alert("Kayıt başarıyla güncellendi!");
                navigate(`/servis-kaydi/${id}`);
            } else {
                await createServiceRecord(serviceRecordData, kullanilanParcalar);
                // Servis oluşturma serviceRecords + transactions + customers (bakiye) +
                // stocks (parça düşümü) yazar; açık listeler eskimesin diye geçersiz kıl.
                await invalidateCollections(['serviceRecords', 'transactions', 'customers', 'stocks']);
                alert("Kayıt başarıyla oluşturuldu!");
                navigate('/servis-panosu');
            }
        } catch (e) {
            console.error("Kayıt kaydedilirken hata oluştu: ", e);
            alert(`Bir hata oluştu: ${e.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    return {
        loading, isEditMode, isSaving, customers, devices,
        selectedCustomerId,
        setSelectedCustomerId: handleCustomerSelect,
        selectedDeviceId,
        setSelectedDeviceId: handleDeviceSelect,
        arizaTanimi, yapilanIslemler, kullanilanParcalar,
        toplamTutar, odenenTutar, odemeTipi, servisNotlari, teslimTarihi,
        bakimTuru, oksijenOrani, basincDegeri, calismaSuresi,
        cihazHurdada, teslimAlmaTarihi, teslimAlmadim, teslimSaati,
        // Setters
        // NOT: selectedCustomerId/selectedDeviceId için akıllı handler'lar
        // (handleCustomerSelect / handleDeviceSelect) yukarıda zaten dışa veriliyor.
        // Buraya tekrar "ham" setter eklemeyin — mükerrer key, cihaz→müşteri
        // otomatik seçimini bozar.
        setArizaTanimi, setYapilanIslemler,
        setOdenenTutar, setOdemeTipi, setServisNotlari, setTeslimTarihi,
        setBakimTuru, setOksijenOrani, setBasincDegeri, setCalismaSuresi,
        setCihazHurdada, setTeslimAlmaTarihi, setTeslimAlmadim, setTeslimSaati,
        // Handlers
        handleMaterialSelect, handleRemoveParca,
        handleMiktarChange, handleFiyatChange, handleSubmit
    };
};
